import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DollarSign } from "lucide-react";
import { type AnalyticsResponse, useAnalytics } from "@/lib/stats";
import { KPICard } from "@/components/dashboard/KPICard";
import { Card } from "@/components/ui/card";
import { ProgressBar } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/tooltip";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type Tone } from "@/lib/status";

/**
 * Deeper dashboard analytics — seller comparison, warmup speed, ban reasons,
 * invite outcomes, cost ROI. The `useAnalytics()` query lives HERE (not on the
 * Dashboard page) so it's only fetched when the Analytics tab is mounted.
 *
 * Sections with no data fall back to a contextual `EmptyState` instead of
 * disappearing — the operator gets a stable layout regardless of history depth.
 */
export function AnalyticsPanels() {
  const { t } = useTranslation();
  const { data, isLoading } = useAnalytics();

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <EmptyState icon={DollarSign} title={t("dashboard.analytics.empty")} />
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <CostBlock data={data} />
      <SellersTable data={data} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <WarmupSpeedTable data={data} />
        <BanReasonsBars data={data} />
      </div>
      <FailingActionsTable data={data} />
      <AgeVsBanBlock data={data} />
    </div>
  );
}

interface Props {
  data: AnalyticsResponse;
}

// ---------------------------------------------------------------------------
// Cost / ROI
// ---------------------------------------------------------------------------

function CostBlock({ data }: Props) {
  const { t } = useTranslation();
  const c = data.cost;

  if (c.accounts_with_cost === 0) {
    return (
      <Section title={t("dashboard.analytics.cost.title")}>
        <Card>
          <EmptyState
            icon={DollarSign}
            title={t("dashboard.analytics.cost.empty_title")}
            description={t("dashboard.analytics.cost.empty_hint")}
          />
        </Card>
      </Section>
    );
  }

  const burnedPct =
    c.total_spent_usd > 0 ? (c.burned_dollars / c.total_spent_usd) * 100 : 0;

  return (
    <Section title={t("dashboard.analytics.cost.title")}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPICard
          label={t("dashboard.analytics.cost.total_spent")}
          value={`$${c.total_spent_usd.toFixed(2)}`}
          hint={t("dashboard.analytics.cost.with_count", {
            count: c.accounts_with_cost,
          })}
        />
        <KPICard
          label={t("dashboard.analytics.cost.burned")}
          value={`$${c.burned_dollars.toFixed(2)}`}
          tone="danger"
          hint={`${burnedPct.toFixed(1)}% ${t("dashboard.analytics.cost.of_total")}`}
        />
        <KPICard
          label={t("dashboard.analytics.cost.avg_active")}
          value={
            c.avg_cost_active_usd != null
              ? `$${c.avg_cost_active_usd.toFixed(2)}`
              : "—"
          }
          tone="success"
        />
        <KPICard
          label={t("dashboard.analytics.cost.avg_dead")}
          value={
            c.avg_cost_dead_usd != null ? `$${c.avg_cost_dead_usd.toFixed(2)}` : "—"
          }
          tone="neutral"
        />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Sellers table — horizontal scroll on overflow
// ---------------------------------------------------------------------------

function SellersTable({ data }: Props) {
  const { t } = useTranslation();
  if (data.sellers.length === 0) {
    return (
      <EmptySection
        title={t("dashboard.analytics.sellers.title")}
        subtitle={t("dashboard.analytics.sellers.subtitle")}
      />
    );
  }
  return (
    <Section
      title={t("dashboard.analytics.sellers.title")}
      subtitle={t("dashboard.analytics.sellers.subtitle")}
    >
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-border bg-bg-secondary text-left text-xs text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.sellers.col.label")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.total")}
                </th>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.sellers.col.survival")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.banned")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.pf")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.avg_cost")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.cost_per_active")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.sellers.col.avg_age")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.sellers.map((s) => (
                <tr
                  key={s.source_label}
                  className="border-b border-border last:border-0 hover:bg-bg-elevated/40"
                >
                  <td className="max-w-[200px] truncate px-3 py-2 font-medium">
                    <Tooltip content={s.source_label}>
                      <span className="truncate">{s.source_label}</span>
                    </Tooltip>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {s.total}
                  </td>
                  <td className="w-[180px] px-3 py-2">
                    <SurvivalBar
                      rate={s.survival_rate}
                      label={`${(s.survival_rate * 100).toFixed(0)}% (${s.active}/${s.total})`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                    {s.banned || "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                    {s.peer_flood_perm || "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {s.avg_cost_usd != null ? `$${s.avg_cost_usd.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {s.cost_per_active_usd != null
                      ? `$${s.cost_per_active_usd.toFixed(2)}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-fg-muted">
                    {s.avg_age_days != null ? `${Math.round(s.avg_age_days)}d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Warmup speed comparison
// ---------------------------------------------------------------------------

function WarmupSpeedTable({ data }: Props) {
  const { t } = useTranslation();
  if (data.warmup_speeds.length === 0) {
    return (
      <EmptySection
        title={t("dashboard.analytics.warmup.title")}
        subtitle={t("dashboard.analytics.warmup.subtitle")}
      />
    );
  }
  return (
    <Section
      title={t("dashboard.analytics.warmup.title")}
      subtitle={t("dashboard.analytics.warmup.subtitle")}
    >
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-bg-secondary text-left text-xs text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.warmup.col.speed")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.warmup.col.total")}
                </th>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.warmup.col.survival")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.warmup.col.completed")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.warmup.col.dead")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.warmup_speeds.map((w) => (
                <tr key={w.speed} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <SpeedBadge speed={w.speed} />
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {w.total}
                  </td>
                  <td className="px-3 py-2">
                    <SurvivalBar
                      rate={w.survival_rate}
                      label={`${(w.survival_rate * 100).toFixed(0)}%`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-success">
                    {w.completed}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                    {w.banned + w.peer_flood_perm}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

function SpeedBadge({ speed }: { speed: string }) {
  const { t } = useTranslation();
  const tone: Tone =
    speed === "natural"
      ? "success"
      : speed === "fast"
        ? "warning"
        : speed === "aggressive"
          ? "danger"
          : "neutral";
  return (
    <Badge tone={tone}>
      {t(`dashboard.analytics.warmup.speed.${speed}`, { defaultValue: speed })}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Ban reasons (bar chart)
// ---------------------------------------------------------------------------

function BanReasonsBars({ data }: Props) {
  const { t } = useTranslation();
  if (data.top_ban_reasons.length === 0) {
    return (
      <EmptySection
        title={t("dashboard.analytics.ban_reasons.title")}
        subtitle={t("dashboard.analytics.ban_reasons.subtitle")}
      />
    );
  }
  const max = Math.max(...data.top_ban_reasons.map((r) => r.count), 1);
  return (
    <Section
      title={t("dashboard.analytics.ban_reasons.title")}
      subtitle={t("dashboard.analytics.ban_reasons.subtitle")}
    >
      <Card className="p-4">
        <div className="space-y-3">
          {data.top_ban_reasons.map((r) => (
            <BarRow key={r.key} label={r.key} value={r.count} max={max} tone="danger" />
          ))}
        </div>
      </Card>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Failing warmup actions
// ---------------------------------------------------------------------------

function FailingActionsTable({ data }: Props) {
  const { t } = useTranslation();
  if (data.failing_actions.length === 0) {
    return (
      <EmptySection
        title={t("dashboard.analytics.actions.title")}
        subtitle={t("dashboard.analytics.actions.subtitle")}
      />
    );
  }
  return (
    <Section
      title={t("dashboard.analytics.actions.title")}
      subtitle={t("dashboard.analytics.actions.subtitle")}
    >
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-bg-secondary text-left text-xs text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.actions.col.action")}
                </th>
                <th className="px-3 py-2 font-medium">
                  {t("dashboard.analytics.actions.col.fail_rate")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.actions.col.failed")}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("dashboard.analytics.actions.col.completed")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.failing_actions.map((a) => (
                <tr key={a.action_type} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 text-xs">
                    {t(`warmup.action.${a.action_type}`, {
                      defaultValue: a.action_type,
                    })}
                  </td>
                  <td className="w-[160px] px-3 py-2">
                    <SurvivalBar
                      rate={1 - a.fail_rate}
                      label={`${(a.fail_rate * 100).toFixed(0)}%`}
                      invert
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-danger">
                    {a.failed}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-success">
                    {a.completed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Invite outcomes bars
// ---------------------------------------------------------------------------

function AgeVsBanBlock({ data }: Props) {
  const { t } = useTranslation();
  if (data.age_buckets.length === 0) return null;
  return (
    <Section
      title={t("dashboard.analytics.age.title")}
      subtitle={t("dashboard.analytics.age.subtitle")}
    >
      <Card className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-5">
        {data.age_buckets.map((b) => (
          <Tooltip
            key={b.bucket}
            content={t("dashboard.analytics.age.from_total", {
              dead: b.banned_or_pf,
              total: b.total,
            })}
          >
            <div className="space-y-1">
              <p className="text-xs text-fg-muted">
                {t(`dashboard.analytics.age.bucket.${b.bucket}`, {
                  defaultValue: b.bucket,
                })}
              </p>
              <p className="font-mono text-2xl font-semibold tabular-nums">
                {(b.survival_rate * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-fg-muted">
                {t("dashboard.analytics.age.from_total", {
                  dead: b.banned_or_pf,
                  total: b.total,
                })}
              </p>
              <SurvivalBar rate={b.survival_rate} compact />
            </div>
          </Tooltip>
        ))}
      </Card>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-fg-muted">
          {title}
        </h2>
        {subtitle && (
          <span className="min-w-0 max-w-full text-xs leading-snug text-fg-muted">
            {subtitle}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * SurvivalBar — context-labeled survival/fail bar built on ProgressBar.
 * Color thresholds: ≥80% success, 50-80% warning, <50% danger. `invert` flips
 * the semantics for fail-rate where low (= action succeeds often) is good.
 */
function SurvivalBar({
  rate,
  label,
  compact,
  invert,
}: {
  rate: number;
  label?: string;
  compact?: boolean;
  invert?: boolean;
}) {
  const pct = Math.max(0, Math.min(1, rate)) * 100;
  const tone: Tone = pct >= 80 ? "success" : pct >= 50 ? "warning" : "danger";
  const inverseTone: Tone =
    pct >= 80 ? "danger" : pct >= 50 ? "warning" : "success";
  return (
    <div className="space-y-0.5">
      <ProgressBar
        value={pct}
        tone={invert ? inverseTone : tone}
        compact={compact ?? true}
      />
      {label && (
        <p className="font-mono text-xs tabular-nums text-fg-muted">{label}</p>
      )}
    </div>
  );
}

function BarRow({
  label,
  value,
  max,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  tone: "danger" | "success";
}) {
  return (
    <ProgressBar
      value={value}
      max={max}
      tone={tone}
      label={
        <Tooltip content={label}>
          <span className="truncate">{label}</span>
        </Tooltip>
      }
      showValue
    />
  );
}

function EmptySection({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  const { t } = useTranslation();
  return (
    <Section title={title} subtitle={subtitle}>
      <Card>
        <EmptyState title={t("dashboard.analytics.empty")} />
      </Card>
    </Section>
  );
}
